/**
 * The arithmetic of an attack, shared by the host's combat (which rolls it),
 * the power score (which estimates it) and enemy damage ratings (which
 * calibrate against it), so the three can't drift apart.
 */
import { BASE_HIT_CHANCE_PERCENT, DEFAULT_UNARMED_BASE_DAMAGE, MAX_HIT_CHANCE_PERCENT, MIN_HIT_CHANCE_PERCENT } from './constants';
import { isSkillActive, normalizeSkill } from './skills';
import type { AttackStyle, CharacterEquipmentLoadout, CoreStats, Equipment, Skill } from './types/game';
import { clamp } from './utils';

/** Hit chance an attack gains per level of its focus skill. */
export const HIT_BONUS_PER_SKILL_LEVEL = 4;

/** Damage an attack gains, as a fraction, per point of its primary stat. */
export const OUTGOING_DAMAGE_PER_ATTRIBUTE_POINT = 0.01;

/** Damage an attack gains, as a fraction, per level of its focus skill. */
export const OUTGOING_DAMAGE_PER_SKILL_LEVEL = 0.02;

/** Base damage of a ranged weapon that sets none. */
export const DEFAULT_RANGED_BASE_DAMAGE = 8;

/** Base damage of an off-hand weapon that sets none. */
export const DEFAULT_OFF_HAND_BASE_DAMAGE = 6;

/** How far a blow's damage varies either side of its base, when its weapon sets nothing. */
export const DEFAULT_DAMAGE_VARIANCE = 0.2;

/** How a weapon attacks: its own style, else ranged for a ranged weapon, else melee (bare hands included). */
export function resolveAttackStyle(weapon: Equipment | undefined): AttackStyle {
    return weapon?.combat?.attackStyle ?? (weapon?.equipmentType === 'range' ? 'range' : 'melee');
}

/**
 * The level an attack of `focus` draws on: the highest of the character's
 * skills with that focus that the loadout lets work (a dual-wield skill needs
 * two weapons). With no loadout every skill counts.
 */
export function resolveFocusSkillLevel(skills: readonly Skill[], focus: Skill['focus'], loadout: CharacterEquipmentLoadout | undefined): number {
    return skills
        .map(normalizeSkill)
        .filter((skill) => skill.focus === focus && (!loadout || isSkillActive(skill, loadout)))
        .reduce((highest, skill) => Math.max(highest, skill.level), 0);
}

/** The core stat an attack of `style` is driven by: Melee, Range, or Conviction for essence. */
export function resolvePrimaryAttribute(coreStats: CoreStats | undefined, style: AttackStyle): number {
    if (!coreStats) {
        return 0;
    }
    switch (style) {
        case 'melee':
            return coreStats.melee;
        case 'range':
            return coreStats.range;
        case 'essence':
            return coreStats.conviction;
    }
}

/**
 * A weapon's base damage per blow: its own, else the default for its hand and
 * kind (an off-hand weapon, a ranged weapon, or bare hands).
 */
export function resolveWeaponBaseDamage(weapon: Equipment | undefined, isOffHand: boolean): number {
    const fallback = isOffHand ? DEFAULT_OFF_HAND_BASE_DAMAGE : weapon?.equipmentType === 'range' ? DEFAULT_RANGED_BASE_DAMAGE : DEFAULT_UNARMED_BASE_DAMAGE;
    return resolvePositiveDamage(weapon?.combat?.baseDamage, fallback);
}

/** A base damage or its fallback: at least 1, and the fallback when it isn't a number. */
export function resolvePositiveDamage(value: number | undefined, fallback: number): number {
    return typeof value === 'number' && Number.isFinite(value) ? Math.max(1, value) : fallback;
}

/** The damage range a blow rolls in: its base, varied by `variancePercent` (0-1) either side, at least 1. */
export function resolveDamageRange(baseDamage: number, variancePercent: number | undefined): { readonly damageMin: number; readonly damageMax: number } {
    const variance = typeof variancePercent === 'number' && Number.isFinite(variancePercent)
        ? clamp(variancePercent, 0, 1)
        : DEFAULT_DAMAGE_VARIANCE;
    const damageMin = Math.max(1, Math.round(baseDamage * (1 - variance)));
    return { damageMin, damageMax: Math.max(damageMin, Math.round(baseDamage * (1 + variance))) };
}

/** An attack's power against a defender's avoidance: its primary stat, weapon accuracy, focus skill and hit bonuses. */
export function resolveAttackPower(primaryAttribute: number, skillLevel: number, accuracy = 0, hitBonus = 0): number {
    return primaryAttribute + accuracy + skillLevel * HIT_BONUS_PER_SKILL_LEVEL + hitBonus;
}

/** The fraction an attack's damage rises by: its primary stat, focus skill and damage multipliers. */
export function resolveOutgoingDamagePercent(primaryAttribute: number, skillLevel: number, damageMultiplier = 0): number {
    return primaryAttribute * OUTGOING_DAMAGE_PER_ATTRIBUTE_POINT + skillLevel * OUTGOING_DAMAGE_PER_SKILL_LEVEL + damageMultiplier;
}

/** The chance, in percent, an attack of `attackPower` lands against `avoidance`, within its floor and ceiling. */
export function resolveHitChancePercent(attackPower: number, avoidance: number): number {
    return clamp(BASE_HIT_CHANCE_PERCENT + attackPower - avoidance, MIN_HIT_CHANCE_PERCENT, MAX_HIT_CHANCE_PERCENT);
}
